import React from 'react';

import { Dropdown, MultiSelectButton } from '@hcs/design-system';
import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import { SpatialSortField } from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateEditCallbackArgs,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatBoolYesNo, formatBoolYesNoUnknown } from '@hcs/utils';

const FIELD = PropertyStateFields.pool;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Pool';
const labelShort = 'Pool';
const propertyStatePath: PropertyStatePaths = '/propertyDetails/poolYN';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  } else if (propertyStateType === 'Core') {
    return propertyState?.propertyDetails?.poolYN;
  }
  return undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatBoolYesNoUnknown(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'Pool';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatBoolYesNo(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) => {
  const value = getValue(propertyStateArgs);
  if (value === null) {
    return `Unknown ${labelShort}`;
  } else if (value) {
    return `Has ${labelShort}`;
  } else {
    return `No ${labelShort}`;
  }
};

// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

const EDIT_OPTIONS = [
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
  {
    label: 'Unknown',
    value: null,
  },
];

// eslint-disable-next-line react-refresh/only-export-components
const Edit = ({
  propertyStateArgs,
  onChange,
  className,
}: PropertyStateArgsProps & {
  onChange: (args: PropertyStateEditCallbackArgs<typeof FIELD>) => void;
  className?: string;
}) => {
  const value = getValue(propertyStateArgs);

  return (
    <Dropdown
      dataHcName="pool-edit-dropdown"
      ariaLabel={`Adjust ${label}`}
      className={className}
      onSelect={(value) => {
        onChange({
          path: propertyStatePath,
          value,
          field: FIELD,
        });
      }}
      options={EDIT_OPTIONS}
      value={value}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return <TableHeaderCell {...props}>{labelShort}</TableHeaderCell>;
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      <Display propertyStateArgs={propertyStateArgs} />
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'left',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

const FILTER_OPTIONS = [
  {
    label: 'Any',
    value: null,
  },
  {
    label: 'Yes',
    value: true,
  },
  {
    label: 'No',
    value: false,
  },
];

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
}: PropertyStateFilterProps<typeof FIELD>) => {
  const value =
    (Array.isArray(spatialFiltersInput.pool)
      ? spatialFiltersInput.pool.find((v) => typeof v === 'boolean')
      : spatialFiltersInput.pool) ?? null;

  return (
    <MultiSelectButton
      dataHcName={`${FIELD}-filter`}
      value={value}
      options={FILTER_OPTIONS}
      disabled={disabled}
      singleSelect={true}
      className={className}
      onChange={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            ...spatialFiltersInput,
            pool: value,
          },
        });
      }}
    />
  );
};

export const POOL_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  labelMicro,
  formatValueShort,
  HeaderCell,
  ContentCell,
  Display,
  Edit,
  Filter,
  spatialSortField: SpatialSortField.Pool,
};
